import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getSponsorEnforcement, setSponsorEnforcement,
  getStudyEnforcement, setStudyEnforcement,
} from '../../services/reviewWorkflowApi';

/**
 * Approval-enforcement toggle (FECOA-1646).
 *
 * Controls whether the review/approval workflow is *required* before content
 * can be published. Tri-state:
 *   - 'default'  -> null  (inherit: study falls back to sponsor, then env default)
 *   - 'on'       -> true  (always require approval)
 *   - 'off'      -> false (never require approval)
 *
 * Scope is chosen by prop: pass `studyId` for the per-study override (wins over
 * the sponsor setting) or `sponsorId` for the sponsor-wide setting. Internal-admin
 * only — the backend re-checks and rejects non-internal users.
 */
const toEnum = (value) => (value === true ? 'on' : value === false ? 'off' : 'default');
const toValue = (enumv) => (enumv === 'on' ? true : enumv === 'off' ? false : null);

const ApprovalSettingsControl = ({ sponsorId, studyId }) => {
  const isStudy = studyId != null;
  const id = isStudy ? studyId : sponsorId;
  const [state, setState] = useState('default');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    if (id == null) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const value = isStudy ? await getStudyEnforcement(id) : await getSponsorEnforcement(id);
        if (active) setState(toEnum(value));
      } catch {
        /* leave at default; backend stays the source of truth */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, isStudy]);

  const handleChange = async (e) => {
    const next = e.target.value;
    const prev = state;
    setState(next);
    setSaving(true);
    try {
      if (isStudy) await setStudyEnforcement(id, toValue(next));
      else await setSponsorEnforcement(id, toValue(next));
      toast.success('Approval enforcement updated.');
    } catch (err) {
      setState(prev);
      toast.error(err?.response?.data?.error || 'Could not update approval enforcement.');
    } finally {
      setSaving(false);
    }
  };

  if (id == null) return null;

  const tip = isStudy
    ? 'Require approval before publishing this study (overrides the sponsor setting)'
    : 'Require approval before publishing assessments for this sponsor';

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg"
      title={tip}
    >
      <ShieldCheck className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <span className="text-sm text-gray-600 dark:text-gray-300 hidden md:inline">Approval</span>
      <select
        value={state}
        onChange={handleChange}
        disabled={loading || saving}
        className="text-sm bg-transparent text-gray-800 dark:text-gray-100 outline-none disabled:opacity-50 cursor-pointer"
      >
        <option value="default">Default {isStudy ? '(inherit sponsor)' : ''}</option>
        <option value="on">Required</option>
        <option value="off">Off</option>
      </select>
    </div>
  );
};

export default ApprovalSettingsControl;
